import React, { useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { aig_audit_evidence_download } from '../services';
import { fmtDate, fmtSize, saveBlob } from './evidenceFormat';

// In-place preview for an evidence file. Reuses the existing download
// endpoint and renders the blob locally -- images and PDFs inline, text
// as plain text, anything else falls back to a download prompt.
//
// Props:
//   file     — { id, file_name, file_type, file_size, uploaded_at, uploaded_by }
//   onClose()
//   flash(msg)

const IMAGE_TYPES = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', bmp: 'image/bmp', svg: 'image/svg+xml', webp: 'image/webp' };
const TEXT_TYPES = { txt: 'text/plain', csv: 'text/csv', log: 'text/plain', json: 'application/json' };
const DOC_TYPES = { doc: true, docx: true, pptx: true, ppt: true };
const PDF_TYPE = 'application/pdf';

const extensionOf = (file) => (
    (file?.file_type || file?.file_name?.split('.').pop() || '').toLowerCase().replace(/^\./, '')
);

export const isPreviewable = (file) => {
    const ext = extensionOf(file);
    return ext === 'pdf' || ext in IMAGE_TYPES || ext in TEXT_TYPES || ext in DOC_TYPES;
};

export default function EvidencePreviewModal({ file, onClose, flash }) {
    const [objectUrl, setObjectUrl] = useState(null);
    const [textContent, setTextContent] = useState(null);
    const [docBlob, setDocBlob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const docRef = useRef(null);

    const ext = extensionOf(file);
    const kind = ext === 'pdf' ? 'pdf' : ext in IMAGE_TYPES ? 'image' : ext in TEXT_TYPES ? 'text' : ext in DOC_TYPES ? 'doc' : 'other';

    useEffect(() => {
        let cancelled = false;
        let createdUrl = null;

        (async () => {
            try {
                const r = await aig_audit_evidence_download(file.id);
                const raw = r?.statusCode || r?.data || r;
                // The blob comes back without a usable content-type, so the
                // extension decides how the browser is told to render it.
                const DOC_MIMES = { doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', ppt: 'application/vnd.ms-powerpoint', pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' };
                const mime = kind === 'pdf' ? PDF_TYPE : kind === 'doc' ? (DOC_MIMES[ext] || 'application/octet-stream') : IMAGE_TYPES[ext] || TEXT_TYPES[ext] || 'application/octet-stream';
                const blob = new Blob([raw], { type: mime });

                if (kind === 'text') {
                    const content = await blob.text();
                    if (!cancelled) setTextContent(content);
                } else if (kind === 'doc') {
                    if (!cancelled) setDocBlob(blob);
                } else {
                    createdUrl = window.URL.createObjectURL(blob);
                    if (!cancelled) setObjectUrl(createdUrl);
                }
            } catch (e) {
                if (!cancelled) setFailed(true);
            }
            if (!cancelled) setLoading(false);
        })();

        return () => {
            cancelled = true;
            if (createdUrl) window.URL.revokeObjectURL(createdUrl);
        };
    }, [file.id, ext, kind]);

    useEffect(() => {
        if (!docBlob || !docRef.current) return;
        docRef.current.innerHTML = '';
        renderAsync(docBlob, docRef.current, null, { inWrapper: false }).catch(() => {});
    }, [docBlob]);

    const download = async () => {
        try {
            const r = await aig_audit_evidence_download(file.id);
            saveBlob(r?.statusCode || r?.data || r, file.file_name);
        } catch (e) { flash?.('Could not download that file.'); }
    };

    const body = () => {
        if (loading) return <div style={centeredNote}>Loading preview…</div>;
        if (failed) return <div style={centeredNote}>Could not load this file.</div>;
        if (kind === 'image') return <img src={objectUrl} alt={file.file_name} style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }} />;
        if (kind === 'pdf') return <iframe title={file.file_name} src={objectUrl} style={{ width: '100%', height: '70vh', border: 0 }} />;
        if (kind === 'text') return <pre style={{ margin: 0, padding: 14, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '70vh', overflow: 'auto', background: 'var(--ap-surface-2)', borderRadius: 4 }}>{textContent}</pre>;
        if (kind === 'doc') return <div ref={docRef} style={{ maxHeight: '70vh', overflow: 'auto', background: '#fff', padding: 14, borderRadius: 4 }} />;
        return <div style={centeredNote}>No preview available for .{ext} files — download it to open.</div>;
    };

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24,
        }}>
            <div onClick={e => e.stopPropagation()} style={{
                background: 'var(--ap-surface)', border: '1px solid var(--ap-border)', borderRadius: 8,
                width: 'min(1000px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: '1px solid var(--ap-border)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--ap-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.file_name}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--ap-text-3)', marginTop: 2 }}>
                            {[file.uploaded_by?.email, fmtDate(file.uploaded_at), fmtSize(file.file_size)].filter(Boolean).join(' · ')}
                        </div>
                    </div>
                    <button className="ap-btn ap-btn--ghost" onClick={download}>Download</button>
                    <button className="ap-btn ap-btn--ghost" onClick={onClose}>Close</button>
                </div>
                <div style={{ padding: 14, overflow: 'auto' }}>{body()}</div>
            </div>
        </div>
    );
}

const centeredNote = { padding: 40, textAlign: 'center', color: 'var(--ap-text-3)', fontSize: 12 };
